import React, { ChangeEvent, KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTimesCircle } from "@fortawesome/pro-light-svg-icons";

import BreadCrumbs from "src/components/BreadCrumbs";
import { Container, NoTicketsMessage } from "src/components/ListScreen";
import DataTable from "src/components/Table";
import TableCell from "src/components/Table/TableCell";
import TableRow from "src/components/Table/TableRow";
import { getUserData } from "src/redux/selectors/user";
import { FirebaseDataService, ResourcePerson, ResourceVendor } from "src/services/Firebase/data";
import { colors, Content, ContentFrame, TopBar } from "src/styles";
import { ID } from "src/types";

import { RESOURCE_TABS, RESOURCES_BREADCRUMBS, ResourceTab } from "./constants";

const TABLE_HEADERS = ["Name", "Role", "Site(s) Assigned"];

interface ResourcesState {
  supervisors: ResourcePerson[];
  technicians: ResourcePerson[];
  cleaners: ResourcePerson[];
  vendors: ResourceVendor[];
}

const EMPTY_STATE: ResourcesState = {
  supervisors: [],
  technicians: [],
  cleaners: [],
  vendors: [],
};

type TableRow_ =
  | { kind: "person"; person: ResourcePerson; profession: string }
  | { kind: "vendor"; vendor: ResourceVendor };

const Resources = () => {
  const navigate = useNavigate();
  const { id: managerId, clientId } = useSelector(getUserData);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const [activeTab, setActiveTab] = useState<ResourceTab>("all");
  const [inputValue, setInputValue] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState<ResourcesState>(EMPTY_STATE);

  const onClickBreadcrumb = (route: ID | null) => typeof route === "string" && navigate(route);

  useEffect(() => {
    setIsLoading(true);
    setError("");
    FirebaseDataService.getResources(managerId, clientId)
      .then((res) => {
        const sortByName = (a: ResourcePerson | ResourceVendor, b: ResourcePerson | ResourceVendor) => {
          const nameA = "firstName" in a ? `${a.firstName} ${a.lastName}`.trim() : a.name;
          const nameB = "firstName" in b ? `${b.firstName} ${b.lastName}`.trim() : b.name;
          return nameA.localeCompare(nameB);
        };

        const sortedSupervisors = res.supervisors?.sort(sortByName) || [];
        const sortedTechnicians = res.technicians?.sort(sortByName) || [];
        const sortedCleaners = res.cleaners?.sort(sortByName) || [];
        const sortedVendors = res.vendors?.sort(sortByName) || [];

        setData({
          supervisors: sortedSupervisors,
          technicians: sortedTechnicians,
          cleaners: sortedCleaners,
          vendors: sortedVendors,
        });
      })
      .catch((err) => setError(err.message))
      .finally(() => setIsLoading(false));
  }, [managerId, clientId]);

  const handleSearch = () => setAppliedSearch(inputValue.trim());

  const handleClear = () => {
    setInputValue("");
    setAppliedSearch("");
    searchInputRef.current?.focus();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSearch();
  };

  const matchesPerson = (p: ResourcePerson, q: string) =>
    `${p.firstName} ${p.lastName}`.toLowerCase().includes(q) ||
    (p.title || "").toLowerCase().includes(q) ||
    (p.sites || []).some((s) => s.toLowerCase().includes(q));

  const matchesVendor = (v: ResourceVendor, q: string) =>
    v.name.toLowerCase().includes(q) || (v.address || "").toLowerCase().includes(q);

  const rows = useMemo<TableRow_[]>(() => {
    const q = appliedSearch.toLowerCase();

    const people = (list: ResourcePerson[], profession: string): TableRow_[] =>
      list.filter((p) => !q || matchesPerson(p, q)).map((person) => ({ kind: "person", person, profession }));

    const vendors = data.vendors
      .filter((v) => !q || matchesVendor(v, q))
      .map((vendor): TableRow_ => ({ kind: "vendor", vendor }));

    switch (activeTab) {
      case "supervisors":
        return people(data.supervisors, "Supervisor");
      case "technicians":
        return people(data.technicians, "Technician");
      case "cleaners":
        return people(data.cleaners, "Custodian");
      case "vendors":
        return vendors;
      default:
        return [
          ...people(data.supervisors, "Supervisor"),
          ...people(data.technicians, "Technician"),
          ...people(data.cleaners, "Custodian"),
          ...vendors,
        ];
    }
  }, [activeTab, data, appliedSearch]);

  const tabCounts: Record<ResourceTab, number | null> = {
    all: data.supervisors.length + data.technicians.length + data.cleaners.length + data.vendors.length || null,
    supervisors: data.supervisors.length || null,
    technicians: data.technicians.length || null,
    cleaners: data.cleaners.length || null,
    vendors: data.vendors.length || null,
  };

  return (
    <Container>
      <TopBar>
        <BreadCrumbs folderStack={RESOURCES_BREADCRUMBS} onClickItem={onClickBreadcrumb} />
      </TopBar>

      <Content>
        <ContentFrame>
          {error && <NoTicketsMessage>{error}</NoTicketsMessage>}

          <ToolbarRow>
            <SearchWrapper>
              <SearchIcon icon={faSearch} />
              <SearchInput
                ref={searchInputRef}
                placeholder="Search by name, profession or site…"
                value={inputValue}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              {(inputValue || appliedSearch) && (
                <ClearIcon icon={faTimesCircle} onClick={handleClear} title="Clear search" />
              )}
            </SearchWrapper>
            <SearchButton onClick={handleSearch} disabled={!inputValue.trim() && !appliedSearch}>
              Search
            </SearchButton>
          </ToolbarRow>

          <TabsRow>
            {RESOURCE_TABS.map((tab) => (
              <TabPill key={tab.key} $isActive={activeTab === tab.key} onClick={() => setActiveTab(tab.key)}>
                {tab.label}
                {tabCounts[tab.key] !== null && (
                  <CountBadge $isActive={activeTab === tab.key}>{tabCounts[tab.key]}</CountBadge>
                )}
              </TabPill>
            ))}
          </TabsRow>

          <DataTable headers={TABLE_HEADERS} isLoading={isLoading}>
            <>
              {rows.map((row, i) => {
                if (row.kind === "vendor") {
                  return (
                    <TableRow key={row.vendor.id} $clickable onClick={() => navigate(`/vendors/${row.vendor.id}`)}>
                      <TableCell>{row.vendor.name}</TableCell>
                      {/* <TableCell>—</TableCell> */}
                      <TableCell>Vendor</TableCell>
                      <TableCell>{row.vendor.address || "—"}</TableCell>
                    </TableRow>
                  );
                }
                const { person, profession } = row;
                return (
                  <TableRow key={`${person.id}-${i}`} $clickable onClick={() => navigate(`/team-members/${person.id}`)}>
                    <TableCell>{`${person.firstName} ${person.lastName}`.trim()}</TableCell>
                    {/* <TableCell>{person.companyName || "—"}</TableCell> */}
                    <TableCell>{person.title || profession}</TableCell>
                    {/* <TableCell>{person.sites?.length ? person.sites.join(", ") : "—"}</TableCell> */}
                    <TableCell>{person.assignedSite ? person.assignedSite : "—"}</TableCell>
                  </TableRow>
                );
              })}
              {!isLoading && rows.length === 0 && (
                <TableRow $clickable={false} onClick={() => {}}>
                  <EmptyCell colSpan={3}>No results found{appliedSearch ? ` for "${appliedSearch}"` : ""}.</EmptyCell>
                </TableRow>
              )}
            </>
          </DataTable>
        </ContentFrame>
      </Content>
    </Container>
  );
};

/* ─── Styles ──────────────────────────────────────────────────────────────── */

const ToolbarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const SearchWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 200px;
  max-width: 460px;
`;

const SearchIcon = styled(FontAwesomeIcon)`
  position: absolute;
  left: 12px;
  color: ${colors.grey.dark4};
  font-size: 14px;
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  height: 40px;
  padding: 0 36px;
  border: 1px solid ${colors.grey.light12};
  border-radius: 6px;
  font-size: 14px;
  color: ${colors.grey.dark};
  background-color: ${colors.white.default};
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;

  &::placeholder {
    color: ${colors.grey.dark4};
  }

  &:focus {
    outline: none;
    border-color: ${colors.kleenway.green};
    box-shadow: 0 0 0 3px rgba(129, 193, 125, 0.15);
  }
`;

const ClearIcon = styled(FontAwesomeIcon)`
  position: absolute;
  right: 11px;
  color: ${colors.grey.dark4};
  font-size: 15px;
  cursor: pointer;
  transition: color 0.15s ease;

  &:hover {
    color: ${colors.grey.dark};
  }
`;

const SearchButton = styled.button`
  height: 40px;
  padding: 0 22px;
  border-radius: 6px;
  border: none;
  background-color: ${colors.kleenway.green};
  color: ${colors.white.default};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s ease;
  white-space: nowrap;

  &:disabled {
    opacity: 0.45;
    cursor: default;
  }

  &:not(:disabled):hover {
    opacity: 0.88;
  }
`;

const TabsRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const TabPill = styled.button<{ $isActive: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 7px 18px;
  border-radius: 20px;
  border: 1.5px solid ${({ $isActive }) => ($isActive ? colors.kleenway.green : colors.grey.light11)};
  background-color: ${({ $isActive }) => ($isActive ? colors.kleenway.green : colors.white.default)};
  color: ${({ $isActive }) => ($isActive ? colors.white.default : colors.grey.dark3)};
  font-size: 13px;
  font-weight: ${({ $isActive }) => ($isActive ? "600" : "400")};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${colors.kleenway.green};
    background-color: ${({ $isActive }) => ($isActive ? colors.kleenway.green : "rgba(129,193,125,0.06)")};
    color: ${({ $isActive }) => ($isActive ? colors.white.default : colors.kleenway.green)};
  }
`;

const CountBadge = styled.span<{ $isActive: boolean }>`
  font-size: 11px;
  font-weight: 700;
  padding: 1px 7px;
  border-radius: 10px;
  background-color: ${({ $isActive }) => ($isActive ? "rgba(255,255,255,0.25)" : colors.grey.light7)};
  color: ${({ $isActive }) => ($isActive ? colors.white.default : colors.grey.dark2)};
`;

const EmptyCell = styled.td`
  padding: 16px 15px;
  font-size: 14px;
  color: ${colors.grey.dark4};
`;

export default Resources;
